import React from 'react';
import {useTranslation} from 'react-i18next';
import Lottie from 'lottie-react';
import {Check} from 'lucide-react';
import {
  Button,
  cn,
  Confetti,
  ResponsiveSheet,
  Tag,
  useModalControls,
} from '@chekinapp/ui';
import {useCustomLotties, useModalOncePerSession} from '@chekin/shared';
import {useSummary} from 'context/summary';
import {useIsSessionModalReady} from 'hooks/useIsSessionModalReady';
import {useReservation} from 'context/reservation';
import {SHOWED_SUCCESS_REGISTER_COMPLETE_MODAL} from 'utils/constants';

const CONFETTI_LAYERS = [
  '[&>i]:[animation-duration:2.6s]',
  '-scale-x-100 [&>i]:[animation-duration:3.3s]',
  'translate-x-[7%] [&>i]:[animation-duration:2.1s]',
  '-translate-x-[5%] -scale-x-100 [&>i]:[animation-duration:3.8s]',
];

function ConfettiBurst() {
  return (
    <>
      {CONFETTI_LAYERS.map(layer => (
        <Confetti key={layer} className={cn(layer, 'z-[100]')} />
      ))}
    </>
  );
}

function CheckInCompleteModal() {
  const {t} = useTranslation();
  const {isGuestappComplete} = useSummary();
  const isReady = useIsSessionModalReady();
  const {data: reservation} = useReservation();
  const {checkinCompletedAnimation} = useCustomLotties();
  const {
    isOpen: isCompleteModalOpen,
    closeModal: closeCompleteModal,
    openModal: openCompleteModal,
  } = useModalControls();

  const {shouldOpenModal, markAsShown} = useModalOncePerSession({
    id: reservation?.id,
    storageKey: SHOWED_SUCCESS_REGISTER_COMPLETE_MODAL,
    isReady,
  });

  const handleClose = React.useCallback(() => {
    markAsShown();
    closeCompleteModal();
  }, [markAsShown, closeCompleteModal]);

  React.useEffect(() => {
    if (shouldOpenModal && isGuestappComplete) openCompleteModal();
  }, [shouldOpenModal, openCompleteModal, isGuestappComplete]);

  return (
    <ResponsiveSheet
      open={isCompleteModalOpen}
      onClose={handleClose}
      title={t('online_checkin_is_completed')}
      className="checkin-completed-modal"
      dialogClassName="sm:max-w-[400px]"
      showCloseButton={false}
      hideTitle
    >
      {checkinCompletedAnimation && <ConfettiBurst />}

      <div className="checkin-completed-modal__container flex flex-col items-center gap-1.5 text-center">
        <div className="mb-1 grid size-[150px] shrink-0 place-items-center">
          {checkinCompletedAnimation && (
            <Lottie animationData={checkinCompletedAnimation} className="size-[150px]" />
          )}
        </div>

        <p className="checkin-completed-modal__title m-0 text-[length:var(--celebration-title-font-size)] font-bold tracking-[-0.015em] text-[color:var(--celebration-title)]">
          {t('online_checkin_is_completed')}
        </p>

        <p className="checkin-completed-modal__subtitle m-0 max-w-[300px] text-[length:var(--celebration-text-font-size)] leading-[var(--celebration-text-line-height)] text-[color:var(--celebration-text)]">
          {t('you_can_keep_using_this_site_to_view_your_information')}
        </p>

        <Tag tone="success" size="sm" className="mt-3">
          <Check />
          {t('enjoy_your_stay')}
        </Tag>
      </div>

      <Button
        size="lg"
        className="checkin-completed-modal__confirm-btn mt-6 w-full"
        onClick={handleClose}
      >
        {t('ok')}
      </Button>
    </ResponsiveSheet>
  );
}

export {CheckInCompleteModal};
